/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { BarChart3, Clock, CheckCircle2, TrendingUp } from "lucide-react";
import RenderSupportPage from "../RenderSupportPage";
import Loader from "../../Components/Loader";
import { supportActionCreators } from "../../services/Actions";

// Head-only reporting: ticket volume, resolution speed and per-agent load.
// Gated at the route (see App.js) and hidden in the sidebar for agents.

function SupportReports() {
  const dispatch = useDispatch();
  const { GetSupportReports } = bindActionCreators(supportActionCreators, dispatch);

  const support = useSelector((state) => state.support);
  const { reports, isLoading } = support;

  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);

  useEffect(() => {
    GetSupportReports({ from, to });
  }, []);

  const summary = [
    { label: "Tickets Received", value: reports?.received ?? 0, icon: <BarChart3 size={18} />, tone: "text-indigo-600 dark:text-indigo-400" },
    { label: "Resolved", value: reports?.resolved ?? 0, icon: <CheckCircle2 size={18} />, tone: "text-emerald-600 dark:text-emerald-400" },
    { label: "Avg First Response", value: reports?.avg_first_response ?? "—", icon: <Clock size={18} />, tone: "text-amber-600 dark:text-amber-400" },
    { label: "Avg Resolution", value: reports?.avg_resolution ?? "—", icon: <TrendingUp size={18} />, tone: "text-violet-600 dark:text-violet-400" },
  ];

  const agentRows = reports?.by_agent || [];

  const inputCls =
    "px-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500";

  return (
    <RenderSupportPage title="Support Reports">
      {isLoading && <Loader />}

      <div className="flex flex-wrap items-end gap-2 -mt-4 mb-5">
        <div>
          <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
        </div>
        <button
          onClick={() => GetSupportReports({ from, to })}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm"
        >
          Run
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summary.map((s) => (
          <div key={s.label} className="p-5 rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className={`mb-3 ${s.tone}`}>{s.icon}</div>
            <p className="text-2xl font-black text-slate-800 dark:text-slate-100 leading-none">{s.value}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 font-semibold">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 dark:border-gray-700">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Agent Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
              <tr>
                {["Agent", "Assigned", "Resolved", "Avg Resolution", "SLA Breaches"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
              {agentRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-14 text-center">
                    <BarChart3 size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      No report data for this period.
                    </p>
                  </td>
                </tr>
              ) : (
                agentRows.map((r, i) => (
                  <tr key={r.agent_id || i} className="hover:bg-slate-50 dark:hover:bg-gray-700/40 transition-colors">
                    <td className="px-4 py-3 text-xs font-bold text-slate-800 dark:text-slate-100">{r.name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">{r.assigned ?? 0}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">{r.resolved ?? 0}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">{r.avg_resolution ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${r.breaches > 0 ? "text-rose-600 dark:text-rose-400" : "text-slate-500 dark:text-slate-400"}`}>
                        {r.breaches ?? 0}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </RenderSupportPage>
  );
}

export default SupportReports;
